import { customElement, state } from "lit/decorators.js";
import { html, TemplateResult } from "lit";
import { LitElementBase } from "../LitElementBase";

@customElement("ehm-context-menu")
export class ContextMenu extends LitElementBase {
  private detailsElement: HTMLDetailsElement | null = null;
  private summaryElement: HTMLElement | null = null;
  private menuItemButtons: NodeListOf<HTMLButtonElement> | null = null;

  @state()
  private open = false;
  @state()
  selectedItem: string | null = null;

  connectedCallback() {
    super.connectedCallback();

    this.detailsElement = this.querySelector("details");
    this.summaryElement = this.querySelector("summary");
    this.menuItemButtons = this.querySelectorAll("button");

    if (!this.detailsElement || !this.summaryElement) {
      throw new Error("ehm-context-menu required details element");
    }

    this.detailsElement.addEventListener("toggle", this.handleDetailsToggle);
    this.summaryElement.addEventListener("keydown", this.handleSummaryKeydown);
    this.detailsElement.addEventListener("keydown", this.handleMenuKeydown);
    this.menuItemButtons?.forEach(button => {
      button.addEventListener("click", this.handleMenuItemClick);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.detailsElement) {
      this.detailsElement.removeEventListener("toggle", this.handleDetailsToggle);
      this.detailsElement.removeEventListener("keydown", this.handleMenuKeydown);
    }

    if (this.summaryElement) {
      this.summaryElement.removeEventListener("keydown", this.handleSummaryKeydown);
    }
    this.menuItemButtons?.forEach(button => {
      button.removeEventListener("click", this.handleMenuItemClick);
    });
  }

  updated() {
    if (this.detailsElement) this.detailsElement.open = this.open;

    if (this.open) {
      document.addEventListener("click", this.handleOutsideClick, true);
    } else {
      document.removeEventListener("click", this.handleOutsideClick, true);
    }
  }

  private handleOutsideClick = (e: Event) => {
    if (this.contains(e.target as Node)) return;
    this.open = false;
  };

  private handleDetailsToggle = (e: Event) => {
    const details = e.currentTarget as HTMLDetailsElement;
    this.open = details.open;
  };

  private handleSummaryKeydown = (e: KeyboardEvent) => {
    const key = e.key;

    if (key === " " || key === "Enter") {
      e.preventDefault();
      this.open = !this.open;
    } else if (this.open && key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      this.focusItem(0);
    } else if (this.open && key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      this.focusItem(this.getMenuItems().length);
    }
  };

  private handleMenuKeydown = (e: KeyboardEvent) => {
    const items = this.getMenuItems();
    if (items.length === 0) return;

    const activeEl = (this.getRootNode() as Document | ShadowRoot).activeElement as HTMLElement | null;
    const currentIndex = items.indexOf(activeEl as HTMLButtonElement);

    if (currentIndex === -1) return;

    const key = e.key;

    const focusWrapped = (nextIndex: number) => {
      const len = items.length;
      const wrapped = ((nextIndex % len) + len) % len;
      this.focusItem(wrapped);
    };

    switch (key) {
      case "ArrowDown":
        e.preventDefault();
        focusWrapped(currentIndex + 1);
        break;

      case "ArrowUp":
        e.preventDefault();
        focusWrapped(currentIndex - 1);
        break;

      case "Home":
        e.preventDefault();
        this.focusItem(0);
        break;

      case "End":
        e.preventDefault();
        this.focusItem(items.length - 1);
        break;

      case " ":
      case "Spacebar":
      case "Enter":
        e.preventDefault();
        (activeEl as HTMLButtonElement).click();
        break;
    }
  };

  private handleMenuItemClick = (e: Event) => {
    this.summaryElement?.focus();
    const target = e.target as HTMLButtonElement;
    if (target.dataset.label) {
      const labelContainer = this.summaryElement?.querySelector("span");
      if (labelContainer) {
        labelContainer.textContent = target.dataset.label;
      }
      this.open = false;
    }
  };

  private getMenuItems(): HTMLButtonElement[] {
    return Array.from(
      this.querySelectorAll<HTMLButtonElement>("button")
    );
  }

  private focusItem(index: number) {
    const items = this.getMenuItems();
    const el = items[index];
    if (el) {
      el.focus();
    }
  }

  render(): TemplateResult {
    return html`
      <slot></slot>
    `;
  }
}